import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Wallet, TrendingUp, CheckCircle2, Clock, ArrowRight, MapPin, AlertTriangle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

export function DriverPayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/drivers/payout");
        setData(data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-4xl mx-auto" data-testid="payout-page">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Earnings</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          Payout ledger
        </h1>
        <p className="text-sm text-zinc-500 mt-3">
          Direct payments from shippers. This is your running tally — money doesn't route through us.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06]">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-[#0F0F10] p-8 h-40 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/[0.06] border border-white/[0.06]" data-testid="payout-stats">
            <StatCard
              label="Pending"
              value={data.pending_usd}
              sub={`${data.jobs_pending} in flight`}
              icon={Clock}
              accent="text-amber-400"
              testid="stat-pending"
            />
            <StatCard
              label="Held — ratings"
              value={data.held_usd}
              sub={data.jobs_held > 0 ? `${data.jobs_held} awaiting both ratings` : "Nothing held"}
              icon={AlertTriangle}
              accent="text-orange-400"
              testid="stat-held"
            />
            <StatCard
              label="Completed"
              value={data.completed_usd}
              sub={`${data.jobs_completed} jobs released`}
              icon={CheckCircle2}
              accent="text-emerald-400"
              testid="stat-completed"
            />
            <StatCard
              label="Lifetime"
              value={data.lifetime_usd}
              sub="All-time"
              icon={TrendingUp}
              accent="text-[#FF3B30]"
              testid="stat-lifetime"
            />
          </div>
          {data.jobs_held > 0 && (
            <div className="mt-4 border border-orange-500/40 bg-orange-500/[0.06] p-4 text-xs text-orange-300 flex items-start gap-2" data-testid="held-notice">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                {data.jobs_held} completed job{data.jobs_held === 1 ? "" : "s"} (${data.held_usd.toLocaleString()})
                {data.jobs_held === 1 ? " is" : " are"} held until both sides rate the haul. Rate yours to release it.
              </span>
            </div>
          )}

          <div className="mt-12">
            <div className="flex items-center gap-3 mb-4">
              <Wallet className="w-4 h-4 text-[#FF3B30]" />
              <h2 className="font-display text-2xl font-black uppercase">Recent jobs</h2>
            </div>
            {data.recent.length === 0 ? (
              <div className="border border-dashed border-white/[0.08] p-10 text-center bg-[#0F0F10]">
                <div className="font-display text-2xl font-black uppercase">No accepted bids yet</div>
                <p className="text-zinc-500 text-sm mt-2">
                  Land your first job and it'll show up here.
                </p>
                <Link
                  to="/app/driver"
                  className="mt-6 inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold uppercase tracking-widest text-xs border-2 border-[#FF3B30] py-3 px-5 transition-all"
                >
                  Browse jobs <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {data.recent.map((r) => (
                  <Link
                    key={r.bid_id}
                    to={`/app/requests/${r.request_id}`}
                    data-testid={`payout-row-${r.bid_id}`}
                    className="group flex items-center justify-between border border-white/[0.08] bg-[#0F0F10] p-4 hover:border-[#FF3B30] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-white truncate">{r.title}</div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500 mt-1">
                        <MapPin className="w-3 h-3 text-[#FF3B30]" />
                        {r.pickup_city} → {r.delivery_city}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <div className="font-display text-lg font-black uppercase">${r.amount_usd.toLocaleString()}</div>
                      {r.held ? (
                        <span
                          data-testid={`held-chip-${r.bid_id}`}
                          className="inline-block mt-1.5 text-[10px] uppercase tracking-[0.25em] font-black px-2 py-0.5 bg-orange-500/90 text-black"
                          title="Held until both sides rate this haul"
                        >
                          Held — rate to release
                        </span>
                      ) : (
                        <StatusChip status={r.status} />
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-[#0F0F10] p-6"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{label}</div>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className="font-display text-5xl font-black uppercase mt-3 leading-none">
        ${value.toLocaleString()}
      </div>
      <div className="text-xs text-zinc-500 mt-2">{sub}</div>
    </motion.div>
  );
}

function StatusChip({ status }) {
  const map = {
    completed: "bg-emerald-500/90 text-black",
    assigned: "bg-amber-500/90 text-black",
    cancelled: "bg-zinc-700 text-zinc-300",
    open: "bg-white/10 text-zinc-300",
  };
  return (
    <span className={`inline-block mt-1.5 text-[10px] uppercase tracking-[0.25em] font-black px-2 py-0.5 ${map[status] || map.open}`}>
      {status}
    </span>
  );
}
